import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { FaPlus, FaEdit, FaTrash, FaImage, FaList, FaSave, FaFileUpload } from 'react-icons/fa';
import { db, storage, auth } from '../../firebase/config';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 1.5rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: ${({ $active, theme }) => $active ? theme.typography.weights.bold : theme.typography.weights.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  transition: ${({ theme }) => theme.transition.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  width: 100%;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  
  @media (min-width: 768px) {
    width: 300px;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 650px;
  border-collapse: collapse;
  margin-bottom: 2rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const ArticleImage = styled.div`
  width: 80px;
  height: 50px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const Status = styled.span`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  background-color: ${({ status, theme }) => 
    status === 'published' ? `${theme.colors.success}15` : 
    status === 'draft' ? `${theme.colors.warning}15` : 
    `${theme.colors.info}15`};
  color: ${({ status, theme }) => 
    status === 'published' ? theme.colors.success : 
    status === 'draft' ? theme.colors.warning : 
    theme.colors.info};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.sizes.md};
  margin-right: 0.5rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23757575' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.default};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}05`};
  }
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  max-width: 100%;
  height: 200px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('journal');
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [user, setUser] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    excerpt: '',
    content: '',
    status: 'draft',
    author: '',
    image: null,
    imageUrl: '',
    preference: '1'
  });
  
  // Check authentication status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // User is signed in, fetch articles
        fetchArticles();
      } else {
        // No user is signed in, sign in anonymously
        signInAnonymously(auth)
          .then((userCredential) => {
            // Signed in anonymously
            setUser(userCredential.user);
            fetchArticles();
          })
          .catch((error) => {
            console.error("Error signing in anonymously:", error);
            setError("Authentication error. Please refresh the page and try again.");
          });
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  const fetchArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      // Check authentication
      if (!auth.currentUser) {
        throw new Error("You must be authenticated to access this content.");
      }
      
      const articlesQuery = query(collection(db, 'journal'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(articlesQuery);
      
      const articlesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().createdAt ? new Date(doc.data().createdAt.toMillis()).toLocaleDateString() : 'Unknown date'
      }));
      
      setArticles(articlesData);
    } catch (err) {
      console.error('Error fetching articles:', err);
      if (err.code === 'permission-denied') {
        setError('Access denied. You may need administrator privileges to view this content.');
      } else {
        setError(`Failed to load articles: ${err.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError("Image file size must be less than 5MB");
        return;
      }
      
      // Validate file type - be more specific about allowed formats
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, or WEBP)");
        return;
      }
      
      setImageFile(file);
      setError(null); // Clear any previous errors
      setUploadProgress(0); // Reset progress bar
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, image: e.target.result }));
      };
      reader.onerror = () => {
        setError("Failed to create image preview");
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadImageToStorage = async (file) => {
    if (!file) return null;
    
    try {
      // Use the Cloud Function for image upload instead of direct Storage access
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'journal');
      
      // Add user ID if available
      if (auth.currentUser) {
        formData.append('userId', auth.currentUser.uid);
      }
      
      // Show upload starting
      console.log(`Starting upload for file: ${file.name}`);
      setUploadProgress(10); // Set initial progress
      
      // Send file to Cloud Function with timeout and error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      try {
        // Send file to Cloud Function
        const response = await fetch('https://uploadimage-jzyeayelrq-uc.a.run.app', {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId); // Clear the timeout
        
        // Progress to 90% when response is received
        setUploadProgress(90);
        
        // Check if response is OK
        if (!response.ok) {
          // Try to get error details from response
          let errorDetails = 'Unknown error';
          try {
            const errorData = await response.json();
            errorDetails = errorData.error || `Server error: ${response.status}`;
          } catch (e) {
            errorDetails = `HTTP error: ${response.status}`;
          }
          
          throw new Error(errorDetails);
        }
        
        // Get the URL from the response
        const data = await response.json();
        setUploadProgress(100); // Set to complete
        
        // Short delay before resetting progress
        setTimeout(() => setUploadProgress(0), 1000);
        
        console.log('Upload successful:', data.url);
        return data.url;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload timed out after 60 seconds. Please try again with a smaller image.');
        }
        throw fetchError;
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Upload failed: ${err.message}`);
      setLoading(false);
      return null;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Check authentication
      if (!auth.currentUser) {
        throw new Error("You must be authenticated to save articles.");
      }
      
      let imageUrl = formData.imageUrl;
      
      // Upload image if a new one is selected
      if (imageFile) {
        try {
          // Set a timeout for the upload
          const uploadPromise = uploadImageToStorage(imageFile);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Image upload timed out. Please try again with a smaller image or check your connection.")), 60000)
          );
          
          imageUrl = await Promise.race([uploadPromise, timeoutPromise]);
          
          if (!imageUrl) {
            throw new Error("Failed to upload image. Please try again.");
          }
        } catch (uploadError) {
          console.error("Image upload error:", uploadError);
          setError(uploadError.message || "Image upload failed. Please try again.");
          setLoading(false);
          return; // Stop the submission if image upload fails
        }
      }
      
      const articleData = {
        title: formData.title,
        category: formData.category,
        excerpt: formData.excerpt,
        content: formData.content,
        status: formData.status,
        author: formData.author,
        imageUrl: imageUrl,
        preference: parseInt(formData.preference),
        updatedAt: serverTimestamp(),
        userId: auth.currentUser.uid // Add user ID for security rules
      };
      
      if (selectedArticle) {
        // Update existing article
        await updateDoc(doc(db, 'journal', selectedArticle.id), articleData);
      } else {
        // Create new article
        articleData.createdAt = serverTimestamp();
        articleData.readTime = `${Math.ceil(formData.content.split(' ').length / 200)} min read`; // Estimate read time
        
        await addDoc(collection(db, 'journal'), articleData);
      }
      
      // Refresh the articles list
      await fetchArticles();
      
      // Reset form
      setShowForm(false);
      setSelectedArticle(null);
      setImageFile(null);
      setFormData({
        title: '',
        category: '',
        excerpt: '',
        content: '',
        status: 'draft',
        author: '',
        image: null,
        imageUrl: '',
        preference: '1'
      });
      
      alert(`Article ${selectedArticle ? 'updated' : 'created'} successfully!`);
    } catch (err) {
      console.error('Error saving article:', err);
      if (err.code === 'permission-denied') {
        setError('Access denied. You may need administrator privileges to save content.');
      } else if (err.code === 'unavailable') {
        setError('Firebase service is currently unavailable. Please try again later.');
      } else {
        setError(`Failed to ${selectedArticle ? 'update' : 'save'} article: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditArticle = (article) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      category: article.category,
      excerpt: article.excerpt,
      content: article.content,
      status: article.status,
      author: article.author,
      image: null, // No preview initially
      imageUrl: article.imageUrl, // Keep the existing URL
      preference: article.preference?.toString() || '1' // Set preference when editing
    });
    setShowForm(true);
  };
  
  const handleDeleteArticle = async (article) => {
    if (window.confirm(`Are you sure you want to delete "${article.title}"?`)) {
      setLoading(true);
      setError(null);
      
      try {
        // Check authentication
        if (!auth.currentUser) {
          throw new Error("You must be authenticated to delete articles.");
        }
        
        await deleteDoc(doc(db, 'journal', article.id));
        await fetchArticles();
        alert('Article deleted successfully!');
      } catch (err) {
        console.error('Error deleting article:', err);
        if (err.code === 'permission-denied') {
          setError('Access denied. You may need administrator privileges to delete content.');
        } else if (err.code === 'unavailable') {
          setError('Firebase service is currently unavailable. Please try again later.');
        } else {
          setError(`Failed to delete article: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Filter articles based on search term
  const filteredArticles = articles.filter(article => 
    article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <PageContainer>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
      
      <Title>Content Management</Title>
      <Subtitle>Manage platform content, articles, and resources.</Subtitle>
      
      <TabContainer>
        <Tab $active={activeTab === 'journal'} onClick={() => setActiveTab('journal')}>
          Journal Articles
        </Tab>
      </TabContainer>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {!showForm ? (
        <>
          <ActionBar>
            <SearchInput 
              type="text" 
              placeholder="Search articles..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button 
              variant="primary" 
              onClick={() => {
                setShowForm(true);
                setSelectedArticle(null);
                setFormData({
                  title: '',
                  category: '',
                  excerpt: '',
                  content: '',
                  status: 'draft',
                  author: '',
                  image: null,
                  imageUrl: '',
                  preference: '1'
                });
              }}
            >
              <FaPlus style={{ marginRight: '0.5rem' }} /> New Article
            </Button>
          </ActionBar>
          
          <Card>
            <Card.Content>
              {articles.length === 0 && !loading ? (
                <p>No articles found. Create your first article by clicking "New Article" button.</p>
              ) : (
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Image</Th>
                        <Th>Title</Th>
                        <Th>Category</Th>
                        <Th>Author</Th>
                        <Th>Date</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredArticles.map(article => (
                        <tr key={article.id}>
                          <Td>
                            {article.imageUrl && <ArticleImage src={article.imageUrl} />}
                          </Td>
                          <Td>{article.title}</Td>
                          <Td>{article.category}</Td>
                          <Td>{article.author}</Td>
                          <Td>{article.date}</Td>
                          <Td>
                            <Status status={article.status}>
                              {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                            </Status>
                          </Td>
                          <Td>
                            <ActionButton onClick={() => handleEditArticle(article)} title="Edit">
                              <FaEdit />
                            </ActionButton>
                            <ActionButton onClick={() => handleDeleteArticle(article)} title="Delete">
                              <FaTrash />
                            </ActionButton>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              )}
            </Card.Content>
          </Card>
        </>
      ) : (
        <Card>
          <Card.Content>
            <form onSubmit={handleSubmit}>
              <h2>{selectedArticle ? 'Edit Article' : 'Create New Article'}</h2>
              
              <FormContainer>
                <div>
                  <FormGroup>
                    <Label htmlFor="title">Title</Label>
                    <Input 
                      type="text" 
                      id="title" 
                      name="title" 
                      value={formData.title} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      id="category" 
                      name="category" 
                      value={formData.category} 
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a category</option>
                      <option value="Nutrition">Nutrition</option>
                      <option value="Health Guide">Health Guide</option>
                      <option value="Research">Research</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Seasonal">Seasonal</option>
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="excerpt">Description</Label>
                    <Textarea 
                      id="excerpt" 
                      name="excerpt" 
                      value={formData.excerpt} 
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="content">Content</Label>
                    <Textarea 
                      id="content" 
                      name="content" 
                      value={formData.content} 
                      onChange={handleInputChange}
                      required
                      style={{ minHeight: "250px" }}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="author">Author</Label>
                    <Input 
                      type="text" 
                      id="author" 
                      name="author" 
                      value={formData.author} 
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="preference">Display Order</Label>
                    <Select 
                      id="preference" 
                      name="preference" 
                      value={formData.preference} 
                      onChange={handleInputChange}
                    >
                      <option value="1">First</option>
                      <option value="2">Second</option>
                      <option value="3">Third</option>
                      <option value="4">Fourth</option>
                      <option value="5">Fifth</option>
                      <option value="6">Sixth</option>
                      <option value="7">Seventh</option>
                      <option value="8">Eighth</option>
                      <option value="9">Ninth</option>
                      <option value="10">Tenth</option>
                    </Select>
                    <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                      Select the order in which this article should appear
                    </small>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="status">Status</Label>
                    <Select 
                      id="status" 
                      name="status" 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="scheduled">Scheduled</option>
                    </Select>
                  </FormGroup>
                </div>
                
                <div>
                  <FormGroup>
                    <Label>Featured Image</Label>
                    <input 
                      type="file" 
                      id="image" 
                      accept="image/*" 
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="image">
                      <ImageUploadArea>
                        <FaImage size={40} color="#757575" />
                        <p style={{ margin: '1rem 0' }}>
                          {imageFile ? imageFile.name : 'Drag and drop an image or click to browse'}
                        </p>
                        <Button type="button" variant="secondary" disabled={loading}>
                          <FaFileUpload style={{ marginRight: '0.5rem' }} /> 
                          {loading && uploadProgress > 0 ? 'Uploading...' : 'Upload Image'}
                        </Button>
                      </ImageUploadArea>
                    </label>
                    
                    {formData.image && (
                      <ImagePreview src={formData.image} />
                    )}
                    
                    {formData.imageUrl && !formData.image && (
                      <>
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>Current image:</p>
                        <ImagePreview src={formData.imageUrl} />
                      </>
                    )}
                    
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div style={{ marginTop: '1rem' }}>
                        <p>Uploading: {Math.round(uploadProgress)}%</p>
                        <div style={{ 
                          height: '6px', 
                          background: '#eee', 
                          borderRadius: '3px', 
                          overflow: 'hidden',
                          marginTop: '0.5rem'
                        }}>
                          <div style={{ 
                            width: `${uploadProgress}%`, 
                            height: '100%', 
                            background: '#4CAF50',
                            transition: 'width 0.3s ease-in-out'
                          }}></div>
                        </div>
                      </div>
                    )}
                    
                    {error && error.includes("image") && (
                      <ErrorMessage>{error}</ErrorMessage>
                    )}
                  </FormGroup>
                </div>
              </FormContainer>
              
              <ButtonGroup>
                <Button type="submit" variant="primary" disabled={loading}>
                  <FaSave style={{ marginRight: '0.5rem' }} /> {selectedArticle ? 'Update Article' : 'Save Article'}
                </Button>
                <Button 
                  type="button" 
                  variant="text" 
                  onClick={() => {
                    setShowForm(false);
                    setSelectedArticle(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </form>
          </Card.Content>
        </Card>
      )}
    </PageContainer>
  );
};

export default ContentManagement; 